import { useState, useRef } from 'react';
import { API_BASE_URL } from '@/config/api';

export default function useCandidateUpload({ jobId, recruiterEmail, onSuccess, onComplete }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFilesStatus, setUploadFilesStatus] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const fakeProgressRef = useRef(0);
  const progressTimerRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const startSmoothProgress = () => {
  fakeProgressRef.current = 0;

  progressTimerRef.current = setInterval(() => {
    fakeProgressRef.current += 90 / 90;

    setOverallProgress(prev =>
      Math.min(
        90,
        Math.max(prev, Math.floor(fakeProgressRef.current))
      )
    );

    if (fakeProgressRef.current >= 90) {
      clearInterval(progressTimerRef.current);
    }
  }, 100); // 100ms tick
};

const stopSmoothProgress = () => {
  if (progressTimerRef.current) {
    clearInterval(progressTimerRef.current);
    progressTimerRef.current = null;
  }
};

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files || []);
  setSelectedFiles(files);
};

const startUpload = () => {
  if (selectedFiles.length === 0) return;
  handleResumeUpload(selectedFiles); // ✅ PASS FILE ARRAY DIRECTLY
};


const resetUpload = () => {
  setSelectedFiles([]);
  setUploadFilesStatus([]);
};


  const handleResumeUpload = async (input) => {
    const files = Array.isArray(input)
        ? input
        : Array.from(input?.target?.files || []);

    // Debug log removed — was leaking uploaded file metadata and jobId to console.
    if (!files.length || !jobId) return;

    const url = `${API_BASE_URL}/api/handle_resume_upload`;

    setIsUploading(true);
    setOverallProgress(0);
    startSmoothProgress();

    const initStatus = files.map(f => ({
      name: f.name,
      progress: 0,
      status: 'queued',
      error: null
    }));
    setUploadFilesStatus(initStatus);

    let successCount = 0;
    let duplicateCount = 0;
    let failedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setUploadFilesStatus(prev =>
        prev.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading', progress: 0 } : f
        )
      );

      const baseProgress = Math.floor((i / files.length) * 90);

      try {
        const result = await uploadSingleFile(
          file,
          jobId,
          recruiterEmail,
          url,
            (p) => {
            
            const perFileProgress = p / 100;
            const realProgress =
            baseProgress + perFileProgress * (90 / files.length);

            setOverallProgress(prev =>
            Math.min(
                Math.floor(Math.max(realProgress, prev)),
                Math.floor(fakeProgressRef.current)
            ));

            setUploadFilesStatus(prev =>
                prev.map((f, idx) =>
                idx === i
                    ? { ...f, progress: Math.floor(perFileProgress * 100) }
                    : f
                )
            );
            }
        );

        setUploadFilesStatus(prev =>
        prev.map((f, idx) =>
            idx === i ? { ...f, status: 'processing', progress: 95 } : f
        )
        );

        await new Promise(r => setTimeout(r, 300));

        // Backend response shape (see app.py /api/handle_resume_upload):
        //   {
        //     "success":         true,
        //     "new_candidates":  [ {...candidate}, ... ],   ← snake_case + plural
        //     "duplicates":      [ {...}, ... ],
        //   }
        //
        // The previous version of this code checked `result.newCandidate`
        // (camelCase + singular) which the backend NEVER sets — every
        // successful upload was therefore flagged as failed even though
        // the candidate was stored. Fix: read the actual field name.
        const isDuplicate = result?.duplicates?.length > 0;
        const isSuccess =
          result?.success === true &&
          Array.isArray(result?.new_candidates) &&
          result.new_candidates.length > 0;

        if (isDuplicate && !isSuccess) {
          duplicateCount++;
          setUploadFilesStatus(prev =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: 'duplicate', progress: 100 } : f
            )
          );
        } else if (isSuccess) {
          successCount++;
          setUploadFilesStatus(prev =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: 'success', progress: 100 } : f
            )
          );
        } else {
          // Real failure path: backend returned success=false OR returned
          // neither a new candidate nor a duplicate. Surface the message.
          failedCount++;
          throw new Error(
            result?.message || result?.error || 'Processing failed',
          );
        }
      } catch (err) {
        setUploadFilesStatus(prev =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'failed', error: err.message } : f
          )
        );
      }
    }

    stopSmoothProgress();
    setOverallProgress(100);
    setIsUploading(false);

    onComplete?.({
        successCount,
        duplicateCount,
        failedCount,
        total: files.length
    });

    if (successCount > 0 && onSuccess) {
      onSuccess();
    }
  };

  return {
    isUploading,
    uploadFilesStatus,
    overallProgress,
    selectedFiles,
    handleFileSelect,
    startUpload,
    resetUpload
  };

}

/* ---- helper (same as yours, unchanged) ---- */
function uploadSingleFile(file, jobId, recruiterEmail, url, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => resolve(JSON.parse(xhr.responseText));
    xhr.onerror = () => reject(new Error('Network error'));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('job_id', jobId);
    formData.append('recruiterEmail', recruiterEmail);

    xhr.open('POST', url);
    xhr.withCredentials = true;
    // Explicit "this is a recruiter upload" declaration. Backend's
    // _resolve_uploader() trusts this header above everything else
    // because only the recruiter UI sends it — eliminates the
    // "admin sessionStorage leaks into recruiter UI" misclassification
    // that was happening when stale X-Admin-Email got attached by a
    // shared sessionStorage from a prior CompanyAdmin login.
    if (recruiterEmail) {
      xhr.setRequestHeader('X-Recruiter-Email', recruiterEmail);
    }
    xhr.send(formData);
  });
}
